import React from 'react';
import { Card, Grid, Message } from 'semantic-ui-react';
import { MdModeEdit } from 'react-icons/md';

// Global Types
import { MyTimesheetEntriesProps } from 'services/myTimesheetEntries/types';

// Global Components
import Bold from 'components/Bold';
import CardActionsBar from 'components/CardActionsBar';
import CurrentMyTimesheetIsEditable from 'components/CurrentMyTimesheetIsEditable';
import MyTimesheetEntryDateValue from 'components/MyTimesheetEntryDateValue';
import MyTimesheetEntryWorkHoursValue from 'components/MyTimesheetEntryWorkHoursValue';
import NestedErrorExistsGate from 'components/NestedErrorExistsGate';
import NestedErrorValue from 'components/NestedErrorValue';
import QueryStringButton from 'components/QueryStringButton';
import SemiBold from 'components/SemiBold';

// Local Components
import MyTimesheetEntryIsEditable from 'components/MyTimesheetEntryIsEditable';
import MyTimesheetEntriesDeleteButton from './components/MyTimesheetEntriesDeleteButton';
import MyTimesheetEntryIsSaved from './components/MyTimesheetEntryIsSaved';
import MyTimesheetEntryLastReviewIsApprovedGate from './components/MyTimesheetEntryLastReviewIsApprovedGate';
import MyTimesheetEntryTotalPaidHoursValue from './components/MyTimesheetEntryTotalPaidHoursValue';
import MyTimesheetEntryWorkTypeNameValue from './components/MyTimesheetEntryWorkTypeNameValue';
import MyTimesheetEntryEntityNameValue from './components/MyTimesheetEntryEntityNameValue';
import MyTimesheetEntryEffectiveRateValue from './components/MyTimesheetEntryEffectiveRateValue';
import MyTimesheetEntryEarningsValue from './components/MyTimesheetEntryEarningsValue';
import MyTimesheetEntryWorkTypeIsPieceRateGate from './components/MyTimesheetEntryWorkTypeIsPieceRateGate';

// Local Constants
import { QUERY_STRING_PARAM, REQUEST_NAMESPACE } from '../../../../constants';
import MyTimesheetEntryGroup from './components/MyTimesheetEntryGroup';

interface OwnProps extends MyTimesheetEntriesProps {
    index?: number;
    myTimesheetStartDate: string;
}

type Props = OwnProps;

export default class MyTimesheetEntriesCardContent extends React.PureComponent<Props> {
    render(): React.ReactElement | null {
        const { myTimesheetEntryUuid, index = 0 } = this.props;

        return (
            <Card.Content>
                {!index && (
                    <Card.Header>
                        <MyTimesheetEntryDateValue
                            myTimesheetEntryUuid={myTimesheetEntryUuid}
                        />
                    </Card.Header>
                )}
                <Card.Description>
                    <CardActionsBar
                        title={
                            <React.Fragment>
                                <Bold>{`TASK ${index + 1}`}</Bold>
                            </React.Fragment>
                        }
                    >
                        <CurrentMyTimesheetIsEditable
                            FallbackComponent={
                                <NestedErrorExistsGate
                                    namespace={REQUEST_NAMESPACE}
                                    uuid={myTimesheetEntryUuid}
                                >
                                    <MyTimesheetEntriesDeleteButton
                                        myTimesheetEntryUuid={
                                            myTimesheetEntryUuid
                                        }
                                    />
                                </NestedErrorExistsGate>
                            }
                        >
                            <MyTimesheetEntryIsEditable
                                myTimesheetEntryUuid={myTimesheetEntryUuid}
                            >
                                <React.Fragment>
                                    <QueryStringButton
                                        params={{
                                            [QUERY_STRING_PARAM]: true,
                                            myTimesheetEntryUuid
                                        }}
                                        buttonProps={{
                                            icon: true,
                                            circular: true,
                                            compact: true
                                        }}
                                    >
                                        <MdModeEdit size="22" />
                                    </QueryStringButton>
                                    <MyTimesheetEntriesDeleteButton
                                        myTimesheetEntryUuid={
                                            myTimesheetEntryUuid
                                        }
                                    />
                                </React.Fragment>
                            </MyTimesheetEntryIsEditable>
                        </CurrentMyTimesheetIsEditable>
                    </CardActionsBar>
                    <Grid>
                        <Grid.Column width={8}>
                            <SemiBold>
                                <MyTimesheetEntryWorkTypeNameValue
                                    myTimesheetEntryUuid={myTimesheetEntryUuid}
                                />
                            </SemiBold>
                        </Grid.Column>
                        <Grid.Column width={8} textAlign="right">
                            <MyTimesheetEntryEntityNameValue
                                myTimesheetEntryUuid={myTimesheetEntryUuid}
                            />
                        </Grid.Column>
                        <Grid.Column width={8}>
                            <SemiBold>
                                <MyTimesheetEntryWorkHoursValue
                                    myTimesheetEntryUuid={myTimesheetEntryUuid}
                                />
                            </SemiBold>
                        </Grid.Column>
                        <Grid.Column width={8} textAlign="right">
                            <MyTimesheetEntryTotalPaidHoursValue
                                myTimesheetEntryUuid={myTimesheetEntryUuid}
                            />
                        </Grid.Column>
                        <Grid.Column width={8}>
                            <SemiBold>Group</SemiBold>
                        </Grid.Column>
                        <Grid.Column width={8} textAlign="right">
                            <MyTimesheetEntryGroup
                                myTimesheetEntryUuid={myTimesheetEntryUuid}
                            />
                        </Grid.Column>
                        <MyTimesheetEntryWorkTypeIsPieceRateGate
                            myTimesheetEntryUuid={myTimesheetEntryUuid}
                        >
                            <Grid.Column width={8}>
                                <SemiBold>Earnings</SemiBold>
                            </Grid.Column>
                            <Grid.Column width={8} textAlign="right">
                                <MyTimesheetEntryEarningsValue
                                    myTimesheetEntryUuid={myTimesheetEntryUuid}
                                />
                            </Grid.Column>
                            <Grid.Column width={8}>
                                <SemiBold>Achieved</SemiBold>
                            </Grid.Column>
                            <Grid.Column width={8} textAlign="right">
                                <MyTimesheetEntryEffectiveRateValue
                                    myTimesheetEntryUuid={myTimesheetEntryUuid}
                                />
                            </Grid.Column>
                        </MyTimesheetEntryWorkTypeIsPieceRateGate>
                        <Grid.Column width={8}>
                            <SemiBold>Status</SemiBold>
                        </Grid.Column>
                        <Grid.Column width={8} textAlign="right">
                            <MyTimesheetEntryLastReviewIsApprovedGate
                                myTimesheetEntryUuid={myTimesheetEntryUuid}
                                FallbackComponent="Approved"
                            >
                                Awaiting Approval
                            </MyTimesheetEntryLastReviewIsApprovedGate>
                            {' / '}
                            <MyTimesheetEntryIsSaved
                                myTimesheetEntryUuid={myTimesheetEntryUuid}
                            />
                        </Grid.Column>
                    </Grid>
                </Card.Description>
                <NestedErrorExistsGate
                    namespace={REQUEST_NAMESPACE}
                    uuid={myTimesheetEntryUuid}
                >
                    <Message negative>
                        <NestedErrorValue
                            namespace={REQUEST_NAMESPACE}
                            uuid={myTimesheetEntryUuid}
                        />
                    </Message>
                </NestedErrorExistsGate>
            </Card.Content>
        );
    }
}
